import React from 'react';

const SOCIAL_FIELDS = [
  { key: 'facebook_url', label: 'Facebook', icon: 'facebook' },
  { key: 'instagram_url', label: 'Instagram', icon: 'instagram' },
  { key: 'linkedin_url', label: 'LinkedIn', icon: 'linkedin' },
  { key: 'youtube_url', label: 'YouTube', icon: 'youtube' },
  { key: 'tiktok_url', label: 'TikTok', icon: 'tiktok' },
];

function SocialIcon({ type }) {
  const paths = {
    facebook: 'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3.5l.5-4H14V7a1 1 0 011-1h3V2z',
    instagram: 'M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm5 5a5 5 0 100 10 5 5 0 000-10zm6.5-.9a1.1 1.1 0 11-2.2 0 1.1 1.1 0 012.2 0z',
    linkedin: 'M6 9h4v12H6V9zm2-6a2.5 2.5 0 110 5 2.5 2.5 0 010-5zM14 9h4v1.7h.1c.6-1.1 2-2.3 4.1-2.3 4.4 0 5.2 2.9 5.2 6.7V21h-4v-6.2c0-1.5 0-3.4-2.1-3.4-2.1 0-2.4 1.6-2.4 3.3V21h-4V9z',
    youtube: 'M21.6 7.2a2.8 2.8 0 00-2-2C17.8 4.5 12 4.5 12 4.5s-5.8 0-7.6.7a2.8 2.8 0 00-2 2A29 29 0 002 12a29 29 0 00.4 4.8 2.8 2.8 0 002 2c1.8.7 7.6.7 7.6.7s5.8 0 7.6-.7a2.8 2.8 0 002-2 29 29 0 00.4-4.8 29 29 0 00-.4-4.8zM10 15.5V8.5l6 3.5-6 3.5z',
    tiktok: 'M16 3c.5 2.5 2 4 5 4v4c-2 0-3.5-.5-5-1.5V15a6 6 0 11-6-6c.3 0 .7 0 1 .1v4.2A2 2 0 1012 15V3h4z',
  };
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d={paths[type] || paths.facebook} />
    </svg>
  );
}

function BadgeList({ items, icon }) {
  if (!items?.length) return null;
  const list = Array.isArray(items) ? items : String(items).split(',').map((s) => s.trim()).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((item, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium"
        >
          {icon === 'star' && <span className="text-amber-500">★</span>}
          {item}
        </span>
      ))}
    </div>
  );
}

export default function AuthorBlock({ content = {} }) {
  const profile = content.agent_profile;
  const visible = content.visible_fields || {};
  const layout = content.layout || 'card';

  if (!profile) {
    return (
      <section className="py-8 px-4 sm:px-0 text-center text-gray-400 text-sm">
        Agent profile unavailable
      </section>
    );
  }

  const show = (field) => Boolean(visible[field]);

  const headshot = show('headshot') && profile.headshot_url ? (
    <img
      src={profile.headshot_url}
      alt={profile.full_name || 'Agent'}
      width={112}
      height={112}
      loading="lazy"
      decoding="async"
      className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-2 border-gray-100"
    />
  ) : null;

  const info = (
    <div className="space-y-3 min-w-0">
      {show('name') && profile.full_name && (
        <h3 className="text-xl font-bold text-gray-900">{profile.full_name}</h3>
      )}
      {show('title') && profile.title && (
        <p className="text-[#00AAFF] font-medium text-sm">{profile.title}</p>
      )}
      {show('bio') && profile.bio && (
        <p className="text-gray-600 text-sm leading-relaxed">{profile.bio}</p>
      )}
      {show('preferred_quote') && profile.preferred_quote && (
        <blockquote className="border-l-4 border-[#00AAFF] pl-4 italic text-gray-700 text-sm">
          &ldquo;{profile.preferred_quote}&rdquo;
        </blockquote>
      )}
      {show('best_income_milestone') && profile.best_income_milestone && (
        <p className="flex items-center gap-2 text-sm text-gray-800">
          <span aria-hidden>🏆</span>
          <span>{profile.best_income_milestone}</span>
        </p>
      )}
      {show('years_in_industry') && profile.years_in_industry != null && (
        <p className="flex items-center gap-2 text-sm text-gray-700">
          <span aria-hidden>📅</span>
          <span>{profile.years_in_industry} years in industry</span>
        </p>
      )}
      {show('clients_served') && profile.clients_served != null && (
        <p className="flex items-center gap-2 text-sm text-gray-700">
          <span aria-hidden>👥</span>
          <span>{profile.clients_served} clients served</span>
        </p>
      )}
      {show('designations') && profile.designations && (
        <BadgeList items={profile.designations} />
      )}
      {show('awards') && profile.awards && (
        <BadgeList items={profile.awards} icon="star" />
      )}
      {show('skills') && profile.skills && (
        <BadgeList items={profile.skills} />
      )}
      <div className="flex flex-wrap gap-3 pt-1">
        {SOCIAL_FIELDS.map(({ key, label, icon }) =>
          show(key) && profile[key] ? (
            <a
              key={key}
              href={profile[key]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-[#00AAFF] transition-colors"
              aria-label={label}
            >
              <SocialIcon type={icon} />
            </a>
          ) : null,
        )}
      </div>
      {show('personal_cta_text') &&
        show('personal_booking_url') &&
        profile.personal_cta_text &&
        profile.personal_booking_url && (
          <a
            href={profile.personal_booking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 px-6 py-3 rounded-lg border-2 border-[#00AAFF] text-[#00AAFF] font-semibold text-sm hover:bg-[#00AAFF] hover:text-white transition-colors"
          >
            {profile.personal_cta_text}
          </a>
        )}
    </div>
  );

  if (layout === 'centered') {
    return (
      <section className="py-8 px-4 sm:px-0">
        <div className="flex flex-col items-center text-center gap-4 max-w-lg mx-auto">
          {headshot}
          {info}
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 px-4 sm:px-0">
      <div className="flex flex-col sm:flex-row gap-6 items-start bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        {headshot && <div className="shrink-0 mx-auto sm:mx-0">{headshot}</div>}
        <div className="flex-1">{info}</div>
      </div>
    </section>
  );
}
